import { Link } from 'react-router-dom';
import { Clock, Heart, MessageCircle, Pin } from 'lucide-react';
import { isVerse, type PostSummary } from '@shared/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn, formatDate, initials } from '@/lib/utils';
import { formLabel, n, t } from '@/lib/i18n';

/**
 * `lead` and `column` are the print variants used on the platform front page;
 * `list` is the original blog-style row, still used on writer sites and author
 * pages where a broadsheet grid would be overkill.
 *
 * `card`, `gallery`, `index` and `bare` came with the site layouts a writer can
 * choose between (see LAYOUT_META). They are variants of this component rather
 * than components of their own because everything they differ in — how much
 * excerpt, whether the cover leads, which metadata survives — is a matter of
 * emphasis on the same handful of fields.
 */
type Variant = 'lead' | 'column' | 'brief' | 'list' | 'card' | 'gallery' | 'index' | 'bare';

interface PostCardProps {
  post: PostSummary;
  variant?: Variant;
  showAuthor?: boolean;
}

export function PostCard({ post, variant = 'list', showAuthor = true }: PostCardProps) {
  if (variant === 'lead') return <LeadStory post={post} showAuthor={showAuthor} />;
  if (variant === 'column') return <ColumnStory post={post} showAuthor={showAuthor} />;
  if (variant === 'brief') return <BriefStory post={post} showAuthor={showAuthor} />;
  if (variant === 'card') return <CardStory post={post} showAuthor={showAuthor} />;
  if (variant === 'gallery') return <GalleryStory post={post} showAuthor={showAuthor} />;
  if (variant === 'index') return <IndexStory post={post} />;
  if (variant === 'bare') return <BareStory post={post} />;
  return <ListStory post={post} showAuthor={showAuthor} />;
}

/**
 * The preview, set the way its form is read.
 *
 * Verse keeps its line breaks. A गजल reflowed into a paragraph stops being a
 * गजल — the breaks are the form, not decoration, and a reader recognises a
 * couplet by its shape before reading a word of it. The server sends verse
 * excerpts with newlines intact (see buildPreview); this is the half that
 * honours them.
 */
function Excerpt({
  post,
  className,
}: {
  post: PostSummary;
  className?: string;
}) {
  const verse = isVerse(post.category);
  return (
    <p className={cn('np', verse && 'whitespace-pre-line', className)}>{post.excerpt}</p>
  );
}

/** The front-page lead: one story, given the room a lead deserves. */
function LeadStory({ post, showAuthor }: { post: PostSummary; showAuthor: boolean }) {
  return (
    <article className="animate-fade-in">
      <Kicker post={post} />

      <h2 className="headline mt-2 text-balance text-4xl sm:text-5xl lg:text-6xl">
        <Link to={post.url} className="transition-colors hover:text-primary">
          {post.title}
        </Link>
      </h2>

      {post.subtitle && (
        <p className="np mt-3 text-balance text-xl leading-snug text-muted-foreground sm:text-2xl">
          {post.subtitle}
        </p>
      )}

      {showAuthor && <Byline post={post} className="mt-4" />}

      {post.coverUrl && (
        <Link to={post.url} className="post-cover mt-5 block">
          <img src={post.coverUrl} alt="" className="halftone w-full object-cover" loading="eager" />
        </Link>
      )}

      {/*
        Prose gets the two-column setting and the drop cap a printed lead would
        have. Verse gets neither: columns would break a stanza across a gutter,
        and a drop cap on the first word of a poem is a typographic category
        error — it belongs to prose openings.
      */}
      {isVerse(post.category) ? (
        <Excerpt post={post} className="mt-5 text-[1.05rem] leading-[1.9]" />
      ) : (
        <div className="print-columns-2 np lead-dropcap mt-5 text-[1.05rem] leading-[1.85]">
          {post.excerpt}
        </div>
      )}

      <Link
        to={post.url}
        className="mt-4 inline-block text-sm font-medium text-primary underline underline-offset-4"
      >{t('पूरा पढ्नुहोस् →')}</Link>
    </article>
  );
}

/** A story in one of the front-page columns. */
function ColumnStory({ post, showAuthor }: { post: PostSummary; showAuthor: boolean }) {
  return (
    <article className="animate-fade-in break-inside-avoid pb-6">
      <Kicker post={post} />

      <h3 className="headline mt-1.5 text-balance text-xl leading-snug">
        <Link to={post.url} className="transition-colors hover:text-primary">
          {post.title}
        </Link>
      </h3>

      {post.coverUrl && (
        <Link to={post.url} className="post-cover mt-3 block">
          <img src={post.coverUrl} alt="" className="halftone h-32 w-full object-cover" loading="lazy" />
        </Link>
      )}

      <Excerpt post={post} className="mt-2 line-clamp-4 text-sm leading-relaxed text-muted-foreground" />

      {showAuthor && <Byline post={post} className="mt-2.5" compact />}
    </article>
  );
}

/** A one-line brief for the bottom of the page. */
function BriefStory({ post, showAuthor }: { post: PostSummary; showAuthor: boolean }) {
  return (
    <article className="rule-thin flex items-baseline gap-3 py-2.5 first:border-t-0">
      <h3 className="headline min-w-0 flex-1 truncate text-base">
        <Link to={post.url} className="hover:text-primary">
          {post.title}
        </Link>
      </h3>
      {showAuthor && (
        <Link
          to={`/author/${post.author.handle}`}
          className="np hidden shrink-0 text-xs text-muted-foreground hover:text-foreground sm:block"
        >
          {post.author.penName || post.author.displayName}
        </Link>
      )}
      <span className="np shrink-0 text-xs text-muted-foreground">{formLabel(post.form)}</span>
    </article>
  );
}

/**
 * Magazine: a card in a two-column grid, cover on top.
 *
 * The cover leads because the grid is what a reader scans, and at this size a
 * photograph is read before a headline is. Without one the card keeps its shape
 * rather than collapsing — a row of cards where some are short and some tall
 * reads as a broken page, not as variety.
 */
function CardStory({ post, showAuthor }: { post: PostSummary; showAuthor: boolean }) {
  return (
    <article className="group animate-fade-in flex flex-col overflow-hidden rounded-lg border transition-colors hover:border-primary/40">
      {post.coverUrl && (
        <Link to={post.url} className="post-cover block overflow-hidden">
          <img
            src={post.coverUrl}
            alt=""
            loading="lazy"
            className="h-44 w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </Link>
      )}

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between gap-2">
          <Kicker post={post} />
          <PinnedMark post={post} />
        </div>

        <h2 className="site-title mt-2 text-balance text-xl font-semibold leading-snug">
          <Link to={post.url} className="transition-colors hover:text-primary">
            {post.title}
          </Link>
        </h2>

        <Excerpt
          post={post}
          className={cn(
            'mt-2 flex-1 text-sm leading-relaxed text-muted-foreground',
            isVerse(post.category) ? 'line-clamp-4' : 'line-clamp-3',
          )}
        />

        <Meta post={post} className="mt-4" />
        {showAuthor && <Byline post={post} className="mt-3" compact />}
      </div>
    </article>
  );
}

/**
 * Gallery: the cover at full width with the title beneath it.
 *
 * For यात्रासाहित्य and anything else photograph-led. A piece with no cover gets
 * a plate of its own accent rather than an empty frame — the grid's rhythm
 * matters more here than any one entry, and a hole in it reads as a failed
 * image load.
 */
function GalleryStory({ post, showAuthor }: { post: PostSummary; showAuthor: boolean }) {
  return (
    <article className="group animate-fade-in">
      <Link to={post.url} className="post-cover block overflow-hidden rounded-lg">
        {post.coverUrl ? (
          <img
            src={post.coverUrl}
            alt=""
            loading="lazy"
            className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="flex aspect-[4/3] w-full items-center justify-center bg-primary/10">
            <span className="np text-sm text-primary/70">{formLabel(post.form)}</span>
          </div>
        )}
      </Link>

      <h2 className="site-title mt-3 text-balance text-lg font-semibold leading-snug">
        <Link to={post.url} className="transition-colors hover:text-primary">
          {post.title}
        </Link>
      </h2>

      <PinnedMark post={post} className="mt-1.5" />
      <Meta post={post} className="mt-1.5" />
      {showAuthor && <Byline post={post} className="mt-2" compact />}
    </article>
  );
}

/**
 * Index: one line per piece.
 *
 * A contents page, for a writer with two hundred मुक्तक behind them. The date is
 * on the left because that is what someone scanning an archive is navigating
 * by; the form label is the only other thing that earns a place on the line.
 *
 * The thumbnail is off in this theme as shipped. It is rendered rather than
 * omitted because `showCovers` is a promise the reader-facing CSS keeps for
 * every layout — a writer who turns covers on and sees nothing change has been
 * given a switch wired to nothing.
 */
function IndexStory({ post }: { post: PostSummary }) {
  return (
    <article className="group flex items-center gap-3 border-b py-2.5 last:border-b-0">
      {post.coverUrl && (
        <Link to={post.url} className="post-cover shrink-0">
          <img src={post.coverUrl} alt="" loading="lazy" className="size-9 rounded object-cover" />
        </Link>
      )}
      <span className="tnum w-24 shrink-0 text-xs text-muted-foreground">{formatDate(post.publishedAt)}</span>
      <h2 className="site-title min-w-0 flex-1 truncate font-medium">
        <Link to={post.url} className="transition-colors hover:text-primary">
          {post.title}
        </Link>
      </h2>
      <PinnedMark post={post} />
      {post.commentCount > 0 && (
        <span className="tnum hidden items-center gap-1 text-xs text-muted-foreground sm:inline-flex">
          <MessageCircle className="size-3" />
          {n(post.commentCount)}
        </span>
      )}
      <span className="np shrink-0 text-xs text-muted-foreground">{formLabel(post.form)}</span>
    </article>
  );
}

/**
 * Minimal: the title, the date, and — if the theme asks for it — a thumbnail.
 *
 * No excerpt on purpose. The whole argument of the minimal layout is that the
 * writing is met on its own page rather than sampled in a list, and a preview
 * is a sample. A cover is not a sample of the writing, which is why it can be
 * turned on here without contradicting that; it stays a thumbnail rather than
 * leading, and the Manuscript theme that uses this layout ships with it off.
 */
function BareStory({ post }: { post: PostSummary }) {
  return (
    <article className="animate-fade-in flex items-start gap-4">
      <div className="min-w-0 flex-1">
        <h2 className="site-title text-balance text-xl leading-snug">
          <Link to={post.url} className="transition-colors hover:text-primary">
            {post.title}
          </Link>
        </h2>
        <div className="mt-1 flex items-center gap-2.5 text-xs text-muted-foreground">
          <span className="tnum">{formatDate(post.publishedAt)}</span>
          <span aria-hidden="true">·</span>
          <span className="np">{formLabel(post.form)}</span>
          <PinnedMark post={post} />
        </div>
      </div>

      {post.coverUrl && (
        <Link to={post.url} className="post-cover hidden shrink-0 sm:block">
          <img src={post.coverUrl} alt="" loading="lazy" className="size-16 rounded object-cover" />
        </Link>
      )}
    </article>
  );
}

/** The row of small numbers under a title: reading time, likes, comments. */
function Meta({ post, className }: { post: PostSummary; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground', className)}>
      <span className="tnum inline-flex items-center gap-1">
        <Clock className="size-3" />
        {t('{count} मिनेट', { count: n(post.readingMinutes) })}
      </span>
      {post.likeCount > 0 && (
        <span className="tnum inline-flex items-center gap-1">
          <Heart className="size-3" />
          {n(post.likeCount)}
        </span>
      )}
      {post.commentCount > 0 && (
        <span className="tnum inline-flex items-center gap-1">
          <MessageCircle className="size-3" />
          {n(post.commentCount)}
        </span>
      )}
    </div>
  );
}

/** The original row, kept for writer sites and author pages. */
function ListStory({ post, showAuthor }: { post: PostSummary; showAuthor: boolean }) {
  return (
    <article className="group animate-fade-in border-b py-7 last:border-b-0">
      <div className="flex gap-6">
        <div className="min-w-0 flex-1">
          {showAuthor && (
            <Link
              to={`/author/${post.author.handle}`}
              className="mb-2 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <Avatar className="size-6">
                {post.author.avatarUrl ? (
                  <AvatarImage src={post.author.avatarUrl} alt={post.author.displayName} />
                ) : null}
                <AvatarFallback className="text-[10px]">
                  {initials(post.author.penName || post.author.displayName)}
                </AvatarFallback>
              </Avatar>
              <span className="np">{post.author.penName || post.author.displayName}</span>
            </Link>
          )}

          <h2 className="site-title text-balance text-2xl font-semibold leading-snug">
            <Link to={post.url} className="transition-colors hover:text-primary">
              {post.title}
            </Link>
          </h2>

          {post.subtitle && <p className="np mt-1 text-muted-foreground">{post.subtitle}</p>}

          {/* The list row is where a writer'"'"'s own site shows their work, so verse
              has to read as verse here too — three lines is enough to recognise. */}
          <Excerpt
            post={post}
            className={cn(
              'mt-2 text-[0.95rem] leading-relaxed text-muted-foreground',
              isVerse(post.category) ? 'line-clamp-3' : 'line-clamp-2',
            )}
          />

          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
            <Badge variant="secondary" className="np">
              {formLabel(post.form)}
            </Badge>
            <PinnedMark post={post} />
            <span>{formatDate(post.publishedAt)}</span>
            <span className="inline-flex items-center gap-1 tnum">
              <Clock className="size-3" />
              {t('{count} मिनेट', { count: n(post.readingMinutes) })}
            </span>
            {post.likeCount > 0 && (
              <span className="inline-flex items-center gap-1 tnum">
                <Heart className="size-3" />
                {n(post.likeCount)}
              </span>
            )}
            {post.commentCount > 0 && (
              <span className="inline-flex items-center gap-1 tnum">
                <MessageCircle className="size-3" />
                {n(post.commentCount)}
              </span>
            )}
            {post.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="np rounded-full bg-muted px-2 py-0.5">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {post.coverUrl && (
          <Link to={post.url} className="post-cover hidden shrink-0 sm:block">
            <img
              src={post.coverUrl}
              alt=""
              loading="lazy"
              className="size-28 rounded-lg object-cover transition-transform group-hover:scale-[1.02]"
            />
          </Link>
        )}
      </div>
    </article>
  );
}

/**
 * The mark on a piece its writer has held at the top of their site.
 *
 * Only ever drawn on the writer's own site — not by a rule here, but because
 * `pinned` is false in everything the platform is sent (see
 * `SerializeOptions.showPin`). Small and set in the accent rather than boxed:
 * it explains why this piece is above a newer one, which is all a reader needs
 * from it.
 */
function PinnedMark({ post, className }: { post: PostSummary; className?: string }) {
  if (!post.pinned) return null;
  return (
    <span className={cn('np inline-flex shrink-0 items-center gap-1 text-xs text-primary', className)}>
      <Pin className="size-3" />
      {t('पिन गरिएको')}
    </span>
  );
}

function Kicker({ post }: { post: PostSummary }) {
  return (
    <div className="kicker flex items-center gap-2">
      <span className="np text-primary">{formLabel(post.form)}</span>
      <span aria-hidden="true">·</span>
      <span className="tnum">{formatDate(post.publishedAt)}</span>
    </div>
  );
}

function Byline({ post, className, compact }: { post: PostSummary; className?: string; compact?: boolean }) {
  return (
    <div className={cn('flex items-center gap-2 text-sm', className)}>
      {!compact && (
        <Avatar className="size-7">
          {post.author.avatarUrl ? (
            <AvatarImage src={post.author.avatarUrl} alt={post.author.displayName} />
          ) : null}
          <AvatarFallback className="text-[10px]">
            {initials(post.author.penName || post.author.displayName)}
          </AvatarFallback>
        </Avatar>
      )}
      <Link to={`/author/${post.author.handle}`} className="np hover:text-primary">
        {post.author.penName || post.author.displayName}
      </Link>
      <span className="text-muted-foreground" aria-hidden="true">
        ·
      </span>
      <span className="tnum text-xs text-muted-foreground">
        {t('{count} मिनेट', { count: n(post.readingMinutes) })}
      </span>
    </div>
  );
}
